import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      const body = e.response?.data;
      const rawDetail = body && typeof body === "object" ? body.detail : undefined;
      const bodyStr = typeof body === "string" ? body : "";
      const ct = (e.response?.headers?.["content-type"] || "").toLowerCase();
      let msg = formatApiError(rawDetail);

      if (!e.response) {
        msg =
          "Não foi possível falar com a API. Suba o backend (uvicorn na porta 8000), o MongoDB e tente de novo.";
      } else if (status >= 502 && status <= 504) {
        msg =
          "502/504: o Nginx não conseguiu falar com a API em 127.0.0.1:8000. Na VPS (SSH): rode sudo bash /opt/pixback/deploy/vps-quick-check.sh — depois journalctl -u pixback-api -n 80, docker ps (Mongo), sudo bash /opt/pixback/deploy/pixback-safety.sh check e systemctl restart pixback-api.";
      } else if (bodyStr.includes("cloudflare") || bodyStr.includes("Cloudflare")) {
        msg =
          "A CDN/proxy recebeu resposta inválida da API (origem). Confirme se o backend está no ar, com Mongo acessível e variáveis MONGO_URL/DB_NAME/JWT_SECRET corretas nos logs de startup.";
      } else if (ct.includes("text/html") || bodyStr.trimStart().startsWith("<!")) {
        msg =
          "A API devolveu HTML em vez de JSON (erro de infraestrutura ou página de erro do proxy). Verifique se a URL do backend está certa e se o serviço subiu.";
      } else if (!msg || msg === "Algo deu errado. Tente novamente.") {
        msg = e.message || "Falha no login";
      }
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true };
    } catch (e) {
      let msg = formatApiError(e.response?.data?.detail);
      if (!e.response) {
        msg =
          "Não foi possível falar com a API. Suba o backend (uvicorn na porta 8000), o MongoDB e tente de novo.";
      } else if (!msg) {
        msg = e.message || "Falha no cadastro";
      }
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
